import { Biryani } from "next/font/google";
import { Provider } from "react-redux";
import { store } from "../store/store";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "lenis/dist/lenis.css";
import Layout from "@/Layout";
import "@/styles/globals.css";
import "@/styles/responsive.css";
import type { AppProps } from "next/app";
import { createContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { reportWebVitals } from "@/utils/webVitals";
import { AIInsightsWidget } from "@/Components/AIInsights";
import {
  AI_INSIGHTS_PAGES,
  AI_INSIGHTS_COMPONENT_TYPES,
} from "@/config/aiInsightsPages";
import dynamic from "next/dynamic";

const GetBusinessModal = dynamic(
  () => import("@/common/modals/GetBusinessModal"),
  { ssr: false },
);

// ✅ Define font at module scope (outside the component)
// Using 'optional' display to prevent layout shifts from font loading
const biryani = Biryani({
  subsets: ["latin"],
  weight: ["200", "300", "400", "600", "700", "800", "900"],
  variable: "--font-biryani",
  display: "optional", // Changed from 'swap' to 'optional' to prevent CLS
  preload: true,
  fallback: [
    "system-ui",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Arial",
    "sans-serif",
  ],
  adjustFontFallback: true, // Automatically adjust fallback font metrics
});

const MicrosoftClarity = dynamic(
  () => import("@/Components/gtm/MicrosoftClarity"),
  {
    ssr: false,
  },
);

type FormContextType = {
  showModal: boolean;
  showForm: () => void;
  hideForm: () => void;
};

export const FormContext = createContext<FormContextType | null>(null);

function useShowAIInsights(componentType?: string) {
  const router = useRouter();
  const pathname = router.asPath.split("?")[0].split("#")[0];

  if (componentType && AI_INSIGHTS_COMPONENT_TYPES.includes(componentType)) {
    return true;
  }

  return AI_INSIGHTS_PAGES.some((pattern) => {
    if (pattern.endsWith("/") && pattern !== "/") {
      const base = pattern.slice(0, -1);
      return pathname === base || pathname.startsWith(pattern);
    }
    return pathname === pattern;
  });
}

const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  const showAIInsights = useShowAIInsights(pageProps?.component);
  const [showModal, setShowModal] = useState(false);

  const formContext: FormContextType = {
    showModal,
    showForm: () => setShowModal(true),
    hideForm: () => setShowModal(false),
  };

  useEffect(() => {
    // Initialize Web Vitals monitoring
    reportWebVitals();
  }, []);

  return (
    <div className={biryani.variable}>
      <Provider store={store as any}>
        <FormContext.Provider value={formContext}>
          <MicrosoftClarity />

          <Layout slug={pageProps?.slug}>
            <Component {...pageProps} />
          </Layout>

          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />

          {showAIInsights && <AIInsightsWidget delay={3000} position="right" />}

          <GetBusinessModal
            show={showModal}
            handleClose={() => setShowModal(false)}
          />
        </FormContext.Provider>
      </Provider>
    </div>
  );
};
export default App;
